/**
 * FL-132: AI配車デモシナリオ
 *
 * 2025年7月22日（火曜日）のAI配車最適化デモンストレーション
 * - 最適化前: 非効率なルーティング・リソース配分
 * - 最適化後: AI による効率化された配車計画
 * - 効果測定: 距離・時間・コスト削減効果
 */

// Demo scenarios for AI dispatch optimization

/**
 * AI配車最適化前の状況（現状のルーティング）
 */
export const BEFORE_OPTIMIZATION = {
  date: '2025-07-22',
  scenario: '最適化前の配車状況',
  description: '手動配車による非効率なルーティング',

  // 未配車案件リスト（最適化対象）
  unassignedCases: [
    {
      caseId: 'case-today-003',
      estimatedDistance: '45km',
      estimatedTime: '2.5時間',
      pickupTime: '10:00-11:00',
      deliveryTime: '15:30',
      priority: 'urgent',
      vehicleType: '普通車',
      route: '千葉県市川市 → 東京都杉並区',
      remarks: '緊急案件 - 手動配車が困難',
    },
    {
      caseId: 'case-today-005',
      estimatedDistance: '42km',
      estimatedTime: '2.3時間',
      pickupTime: '14:00-15:00',
      deliveryTime: '17:00',
      priority: 'high',
      vehicleType: '普通車',
      route: '東京都板橋区 → 埼玉県所沢市',
      remarks: '車検切れ対応 - 仮ナンバー必要',
      specialRequirements: ['仮ナンバー対応可能ドライバー'],
    },
    {
      caseId: 'case-today-006',
      estimatedDistance: '38km',
      estimatedTime: '2.1時間',
      pickupTime: '17:00-18:00',
      deliveryTime: '19:30',
      priority: 'normal',
      vehicleType: 'SUV',
      route: '千葉県船橋市 → 東京都足立区',
      remarks: '夕方配送 - 渋滞考慮必要',
    },
    {
      caseId: 'case-today-007',
      estimatedDistance: '35km',
      estimatedTime: '2.0時間',
      pickupTime: '15:30-16:30',
      deliveryTime: '18:00',
      priority: 'urgent',
      vehicleType: '普通車',
      route: '東京都大田区 → 埼玉県さいたま市',
      remarks: '緊急追加案件',
    },
    {
      caseId: 'case-today-008',
      estimatedDistance: '32km',
      estimatedTime: '1.8時間',
      pickupTime: '18:30-19:30',
      deliveryTime: '20:30',
      priority: 'low',
      vehicleType: '普通車',
      route: '東京都大田区 → 神奈川県横浜市',
      remarks: '週末前最終配送',
    },
  ],

  // 利用可能な陸送員リスト
  availableTransporters: [
    {
      transporterId: 'lt-driver-c-001',
      name: '鈴木 次郎',
      status: 'available',
      currentLocation: '埼玉県さいたま市',
      workingHours: '08:30-17:30',
      remainingCapacity: '70%',
      capabilities: ['普通車', '軽自動車'],
      specialSkills: [],
    },
    {
      transporterId: 'lt-driver-d-001',
      name: '高橋 三郎',
      status: 'available',
      currentLocation: '千葉県市川市',
      workingHours: '20:00-05:00',
      remainingCapacity: '100%',
      capabilities: ['普通車', 'SUV', '軽自動車'],
      specialSkills: ['仮ナンバー対応', '夜勤', '長距離'],
    },
    {
      transporterId: 'lt-driver-f-001',
      name: '小林 美香',
      status: 'available',
      currentLocation: '東京都練馬区',
      workingHours: '09:00-13:00',
      remainingCapacity: '60%',
      capabilities: ['普通車', '軽自動車'],
      specialSkills: ['午前中配送'],
    },
  ],

  // 最適化前の問題点
  issues: [
    {
      type: 'routing_inefficiency',
      description: '非効率なルーティング',
      impact: '総走行距離192km（平均38.4km/案件）',
    },
    {
      type: 'time_overlap',
      description: '時間枠の重複',
      impact: '案件003と005で同時間帯競合',
    },
    {
      type: 'skill_mismatch',
      description: 'スキルとのミスマッチ',
      impact: '仮ナンバー対応可能ドライバーの未活用',
    },
    {
      type: 'capacity_waste',
      description: 'キャパシティの無駄',
      impact: '午前専門ドライバーが午後案件対応不可',
    },
    {
      type: 'urgent_delay',
      description: '緊急案件の遅延リスク',
      impact: '緊急案件2件が未配車状態',
    },
  ],

  // 最適化前の統計
  stats: {
    totalCases: 5,
    totalDistance: '192km',
    totalTime: '10.7時間',
    averageDistancePerCase: '38.4km',
    availableTransporters: 3,
    utilizationRate: '45%',
    urgentCasesunassigned: 2,
    estimatedCompletionTime: '20:30',
  },
} as const;

/**
 * AI配車最適化後の状況（効率化されたルーティング）
 */
export const AFTER_OPTIMIZATION = {
  date: '2025-07-22',
  scenario: 'AI最適化後の配車計画',
  description: 'AIによる効率化されたルーティングと最適なリソース配分',

  // 最適化された配車プラン
  optimizedAssignments: [
    {
      transporterId: 'lt-driver-d-001',
      transporterName: '高橋 三郎',
      assignedCases: [
        {
          caseId: 'case-today-005',
          sequence: 1,
          optimizedDistance: '35km', // 42km → 35km（7km削減）
          optimizedTime: '1.9時間', // 2.3時間 → 1.9時間（0.4時間削減）
          pickupTime: '14:00-15:00',
          deliveryTime: '16:30', // 17:00 → 16:30（30分短縮）
          route: '東京都板橋区 → 埼玉県所沢市',
          optimizationReason: '仮ナンバー対応スキル活用、ルート最適化',
        },
        {
          caseId: 'case-today-006',
          sequence: 2,
          optimizedDistance: '31km', // 38km → 31km（7km削減）
          optimizedTime: '1.7時間', // 2.1時間 → 1.7時間（0.4時間削減）
          pickupTime: '17:00-18:00',
          deliveryTime: '19:00', // 19:30 → 19:00（30分短縮）
          route: '千葉県船橋市 → 東京都足立区',
          optimizationReason: '夜勤シフト活用、渋滞回避ルート選択',
        },
      ],
      totalDistance: '66km',
      totalTime: '3.6時間',
      utilizationRate: '75%',
      specialSkillsUsed: ['仮ナンバー対応', '夜勤'],
    },
    {
      transporterId: 'lt-driver-c-001',
      transporterName: '鈴木 次郎',
      assignedCases: [
        {
          caseId: 'case-today-003',
          sequence: 1,
          optimizedDistance: '38km', // 45km → 38km（7km削減）
          optimizedTime: '2.1時間', // 2.5時間 → 2.1時間（0.4時間削減）
          pickupTime: '10:00-11:00',
          deliveryTime: '14:30', // 15:30 → 14:30（1時間短縮）
          route: '千葉県市川市 → 東京都杉並区',
          optimizationReason: '緊急案件優先、効率ルート選択',
        },
        {
          caseId: 'case-today-007',
          sequence: 2,
          optimizedDistance: '29km', // 35km → 29km（6km削減）
          optimizedTime: '1.6時間', // 2.0時間 → 1.6時間（0.4時間削減）
          pickupTime: '15:30-16:30',
          deliveryTime: '17:30', // 18:00 → 17:30（30分短縮）
          route: '東京都大田区 → 埼玉県さいたま市',
          optimizationReason: '緊急案件連続処理、地域集約効果',
        },
      ],
      totalDistance: '67km',
      totalTime: '3.7時間',
      utilizationRate: '80%',
      specialSkillsUsed: ['緊急対応'],
    },
    {
      transporterId: 'lt-driver-f-001',
      transporterName: '小林 美香',
      assignedCases: [
        {
          caseId: 'case-today-008',
          sequence: 1,
          optimizedDistance: '28km', // 32km → 28km（4km削減）
          optimizedTime: '1.5時間', // 1.8時間 → 1.5時間（0.3時間削減）
          pickupTime: '11:00-12:00', // 18:30-19:30 → 11:00-12:00（時間変更）
          deliveryTime: '13:00', // 20:30 → 13:00（大幅短縮）
          route: '東京都大田区 → 神奈川県横浜市',
          optimizationReason: '午前中対応活用、渋滞回避',
        },
      ],
      totalDistance: '28km',
      totalTime: '1.5時間',
      utilizationRate: '65%',
      specialSkillsUsed: ['午前中集中対応'],
    },
  ],

  // 最適化後の改善効果
  improvements: [
    {
      type: 'distance_reduction',
      description: '総走行距離の削減',
      before: '192km',
      after: '161km',
      improvement: '31km削減（16.1%減）',
    },
    {
      type: 'time_efficiency',
      description: '配送時間の短縮',
      before: '10.7時間',
      after: '8.8時間',
      improvement: '1.9時間短縮（17.8%減）',
    },
    {
      type: 'completion_acceleration',
      description: '完了時刻の前倒し',
      before: '20:30',
      after: '19:00',
      improvement: '1.5時間前倒し',
    },
    {
      type: 'urgent_priority',
      description: '緊急案件の優先処理',
      before: '2件未配車',
      after: '2件とも14:30と17:30に完了',
      improvement: '緊急案件100%当日完了',
    },
    {
      type: 'skill_optimization',
      description: 'スキルマッチング最適化',
      before: '仮ナンバー対応スキル未活用',
      after: '適切なドライバーが車検切れ車両対応',
      improvement: 'スペシャリスト活用100%',
    },
    {
      type: 'resource_utilization',
      description: 'リソース活用率向上',
      before: '45%',
      after: '73%',
      improvement: '28ポイント向上',
    },
  ],

  // 最適化後の統計
  stats: {
    totalCases: 5,
    totalDistance: '161km',
    totalTime: '8.8時間',
    averageDistancePerCase: '32.2km',
    utilizedTransporters: 3,
    utilizationRate: '73%',
    urgentCasescompleted: 2,
    estimatedCompletionTime: '19:00',
    costSavings: '約15%のコスト削減',
  },
} as const;

/**
 * AI配車最適化アルゴリズムの詳細
 */
export const OPTIMIZATION_ALGORITHM = {
  name: 'ラクハイAI配車エンジン',
  version: '1.0',

  // 最適化要素
  optimizationFactors: [
    {
      factor: 'distance_minimization',
      weight: 30,
      description: '総走行距離の最小化',
    },
    {
      factor: 'time_efficiency',
      weight: 25,
      description: '配送時間の効率化',
    },
    {
      factor: 'skill_matching',
      weight: 20,
      description: 'ドライバースキルとのマッチング',
    },
    {
      factor: 'priority_handling',
      weight: 15,
      description: '案件優先度の考慮',
    },
    {
      factor: 'resource_balancing',
      weight: 10,
      description: 'リソース負荷の均等化',
    },
  ],

  // 制約条件
  constraints: [
    'ドライバーの勤務時間制限',
    '車両種別の対応可能性',
    '特殊スキル要件（仮ナンバー等）',
    '案件の時間指定',
    '緊急案件の優先処理',
    '地域別の渋滞状況',
  ],

  // AIモデル情報
  aiModel: {
    type: 'Hybrid Optimization',
    components: [
      'Google Gemini 2.5-flash（ルート最適化）',
      'Google Maps Platform（距離・時間計算）',
      'カスタム制約ソルバー（スキルマッチング）',
    ],
    processingTime: '平均3.2秒',
  },
} as const;

/**
 * デモシナリオの実行フロー
 */
export const DEMO_EXECUTION_FLOW = {
  phases: [
    {
      phase: 1,
      name: '現状把握',
      duration: '30秒',
      description: '未配車案件と利用可能リソースの確認',
      actions: ['5件の未配車案件表示', '3名の利用可能ドライバー表示', '問題点の可視化'],
    },
    {
      phase: 2,
      name: 'AI最適化実行',
      duration: '3秒',
      description: 'AIエンジンによる配車最適化',
      actions: ['AI配車ボタンクリック', '最適化計算の実行', '結果の生成'],
    },
    {
      phase: 3,
      name: '結果表示',
      duration: '60秒',
      description: '最適化結果の詳細表示',
      actions: ['配車プランの表示', '改善効果の数値表示', 'ルートマップの表示'],
    },
    {
      phase: 4,
      name: '効果確認',
      duration: '30秒',
      description: '最適化効果の確認',
      actions: ['距離・時間削減効果', 'コスト削減効果', 'ユーザビリティ向上'],
    },
  ],
  totalDemoTime: '約2分',
} as const;

/**
 * 全AI配車デモシナリオデータ
 */
export const AI_DISPATCH_SCENARIOS = {
  BEFORE: BEFORE_OPTIMIZATION,
  AFTER: AFTER_OPTIMIZATION,
  ALGORITHM: OPTIMIZATION_ALGORITHM,
  DEMO_FLOW: DEMO_EXECUTION_FLOW,
} as const;

export default AI_DISPATCH_SCENARIOS;
